/**
 * WeaveIn.ai Platform - Document Category & Type Manager
 * Admin UI for configuring document categories, types, metadata fields,
 * and attribution rules.
 *
 * Document types carry upload_roles/view_roles columns, but nothing has ever
 * read them to make an access decision — document access is gated on the
 * organization alone. The "Role-Based Access" checkboxes that used to live here
 * therefore promised a restriction that did not exist, so they were removed
 * (TRUS-436). The columns remain in the API and schema so no configuration was
 * destroyed; re-enabling means enforcing them server-side first.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, ChevronDown, ChevronRight, X,
  FolderOpen, FileText, Settings2, Users, Calendar,
  GripVertical, Check, AlertCircle, Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { getWeaveinApiUrl } from '@/src/config';
import { DocSubtypeList } from './DocSubtypeList';

// ============================================================================
// TYPES
// ============================================================================

interface DocCategory {
  id: number;
  cbo_id: number;
  name: string;
  description: string | null;
  scope: 'member' | 'staff' | 'both';
  sort_order: number;
  is_active: boolean;
  doc_type_count: number;
}

interface ProgramOption {
  id: number;
  name: string;
}

interface MetadataField {
  field_id: string;
  field_type: string;
  label: string;
  description?: string;
  required: boolean;
  options?: string[];
  order: number;
}

interface DocType {
  id: number;
  cbo_id: number;
  category_id: number;
  category_name: string;
  name: string;
  description: string | null;
  scope: 'member' | 'staff' | 'both';
  due_date_rules: any;
  is_notes_required: boolean;
  metadata_fields: MetadataField[];
  attribution_levels: string[];
  allowed_file_types: string[];
  max_file_size_mb: number | null;
  is_active: boolean;
  sort_order: number;
  program_ids: number[];
  document_count: number;
  requires_hard_copy_workflow: boolean;
  hard_copy_settings: Record<string, any>;
}

// ============================================================================
// API HELPERS
// ============================================================================

// A new type accepts program and enrollment attribution unless the admin unticks
// them. The old default was ['none'] alone, which reads in the list as
// "Attribution: None" and means create_document rejects a program_id and silently
// STRIPS enrollment_id — so a document filed under the type can never reach the
// enrollment it belongs to. Four orgs had to be repaired by migration for taking
// that default (682 IFR, 683 Maui LTRG); it must match the server's DocTypeCreate
// default. 'appointment' stays opt-in: create_document hard-rejects an
// appointment_id on a type that does not list it.
const DEFAULT_ATTRIBUTION_LEVELS = ['none', 'program', 'enrollment'];

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

// ============================================================================
// COMPONENT
// ============================================================================

interface DocumentCategoryManagerProps {
  organizationId?: number;
}

export function DocumentCategoryManager({ organizationId }: DocumentCategoryManagerProps) {
  const [categories, setCategories] = useState<DocCategory[]>([]);
  const [docTypes, setDocTypes] = useState<Record<number, DocType[]>>({});
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  // null = edit the organization's own catalog. A program id switches every
  // sub-type list below into that program's definition of it.
  const [activeProgramId, setActiveProgramId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  // Category dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<DocCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '', description: '', scope: 'both' as const,
  });

  // Doc type dialog
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<DocType | null>(null);
  const [typeForm, setTypeForm] = useState<any>({
    category_id: 0, name: '', description: '', scope: 'both',
    is_notes_required: false,
    metadata_fields: [],
    attribution_levels: DEFAULT_ATTRIBUTION_LEVELS,
    allowed_file_types: ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'docx', 'xlsx', 'pptx'],
    max_file_size_mb: null,
    program_ids: [],
  });

  // ========================================================================
  // DATA FETCHING
  // ========================================================================

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(getWeaveinApiUrl('/api/doc-categories?include_inactive=true'), {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch categories');
      const data = await res.json();
      setCategories(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDocTypes = useCallback(async (categoryId: number) => {
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/doc-types?category_id=${categoryId}&include_inactive=true`),
        { credentials: 'include', headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error('Failed to fetch document types');
      const data = await res.json();
      setDocTypes((prev) => ({ ...prev, [categoryId]: data }));
    } catch (e: any) {
      setError(e.message);
    }
  }, []);

  // The org's programs, for restricting a type to specific ones. Fetched once
  // with the page rather than when the dialog opens, so the list is already
  // there when an admin clicks Edit. A failure is not surfaced as a page error:
  // it degrades the Programs section to "no programs configured", which is the
  // same thing an org with no programs sees, and everything else in the dialog
  // still works.
  const fetchPrograms = useCallback(async () => {
    try {
      const res = await fetch(getWeaveinApiUrl('/api/programs?is_active=true'), {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) {
        setPrograms(data.map((p: any) => ({ id: p.id, name: p.name })));
      }
    } catch {
      // Non-fatal — see above.
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchPrograms();
  }, [fetchCategories, fetchPrograms]);

  // ========================================================================
  // CATEGORY CRUD
  // ========================================================================

  const toggleCategory = (id: number) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (!docTypes[id]) fetchDocTypes(id);
      }
      return next;
    });
  };

  const openCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', description: '', scope: 'both' });
    setCategoryDialogOpen(true);
  };

  const openEditCategory = (cat: DocCategory) => {
    setEditingCategory(cat);
    setCategoryForm({ name: cat.name, description: cat.description || '', scope: cat.scope });
    setCategoryDialogOpen(true);
  };

  const saveCategory = async () => {
    try {
      const url = editingCategory
        ? getWeaveinApiUrl(`/api/doc-categories/${editingCategory.id}`)
        : getWeaveinApiUrl('/api/doc-categories');
      const method = editingCategory ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(categoryForm),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save category');
      }

      const saved = await res.json();
      setCategoryDialogOpen(false);
      fetchCategories();

      // A brand-new category has no document types, and nothing can be uploaded
      // to a category without one (TRUS-436 — Maui and VYC each have two sitting
      // in that state, one of them seeded by a migration). Rather than leave the
      // admin to discover that later, go straight into adding the first type.
      if (!editingCategory && saved?.id) {
        setExpandedCategories((prev) => new Set(prev).add(saved.id));
        openCreateType(saved.id);
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  const deactivateCategory = async (id: number) => {
    if (!confirm('Deactivate this category and all its document types?')) return;
    try {
      await fetch(getWeaveinApiUrl(`/api/doc-categories/${id}`), {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      fetchCategories();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ========================================================================
  // DOC TYPE CRUD
  // ========================================================================

  const openCreateType = (categoryId: number) => {
    setEditingType(null);
    setTypeForm({
      category_id: categoryId, name: '', description: '', scope: 'both',
      is_notes_required: false, metadata_fields: [],
      attribution_levels: DEFAULT_ATTRIBUTION_LEVELS,
      allowed_file_types: ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'docx', 'xlsx', 'pptx'],
      max_file_size_mb: null,
      requires_hard_copy_workflow: false,
      program_ids: [],
    });
    setTypeDialogOpen(true);
  };

  const openEditType = (dt: DocType) => {
    setEditingType(dt);
    setTypeForm({
      category_id: dt.category_id, name: dt.name, description: dt.description || '',
      scope: dt.scope,
      is_notes_required: dt.is_notes_required, metadata_fields: dt.metadata_fields,
      attribution_levels: dt.attribution_levels,
      allowed_file_types: dt.allowed_file_types,
      max_file_size_mb: dt.max_file_size_mb,
      requires_hard_copy_workflow: dt.requires_hard_copy_workflow || false,
      program_ids: dt.program_ids || [],
    });
    setTypeDialogOpen(true);
  };

  const saveDocType = async () => {
    try {
      const url = editingType
        ? getWeaveinApiUrl(`/api/doc-types/${editingType.id}`)
        : getWeaveinApiUrl('/api/doc-types');
      const method = editingType ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(typeForm),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save document type');
      }

      setTypeDialogOpen(false);
      if (typeForm.category_id) fetchDocTypes(typeForm.category_id);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const deactivateDocType = async (dt: DocType) => {
    if (!confirm(`Deactivate "${dt.name}"? Historical documents are preserved.`)) return;
    try {
      await fetch(getWeaveinApiUrl(`/api/doc-types/${dt.id}`), {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      fetchDocTypes(dt.category_id);
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ========================================================================
  // METADATA FIELD MANAGEMENT
  // ========================================================================

  const addMetadataField = () => {
    const newField: MetadataField = {
      field_id: `field_${Date.now()}`,
      field_type: 'text',
      label: '',
      required: false,
      order: typeForm.metadata_fields.length,
    };
    setTypeForm({ ...typeForm, metadata_fields: [...typeForm.metadata_fields, newField] });
  };

  const updateMetadataField = (index: number, updates: Partial<MetadataField>) => {
    const fields = [...typeForm.metadata_fields];
    fields[index] = { ...fields[index], ...updates };
    setTypeForm({ ...typeForm, metadata_fields: fields });
  };

  const removeMetadataField = (index: number) => {
    const fields = typeForm.metadata_fields.filter((_: any, i: number) => i !== index);
    setTypeForm({ ...typeForm, metadata_fields: fields });
  };

  // ========================================================================
  // ROLE TOGGLE
  // ========================================================================


  const toggleAttribution = (level: string) => {
    const current: string[] = typeForm.attribution_levels;
    const updated = current.includes(level)
      ? current.filter((l: string) => l !== level)
      : [...current, level];
    setTypeForm({ ...typeForm, attribution_levels: updated });
  };

  // ========================================================================
  // PROGRAM RESTRICTION
  // ========================================================================

  // An EMPTY selection means "available to every program", not "available to
  // none" — restricting a type is an explicit act, and the unrestricted state has
  // to stay the default because that is what every existing type is. Untick the
  // last program and the type goes back to being offered everywhere.
  const toggleProgram = (programId: number) => {
    const current: number[] = typeForm.program_ids || [];
    const updated = current.includes(programId)
      ? current.filter((id: number) => id !== programId)
      : [...current, programId];
    setTypeForm({ ...typeForm, program_ids: updated });
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  const scopeBadge = (scope: string) => {
    const colors: Record<string, string> = {
      member: 'bg-blue-100 text-blue-800',
      staff: 'bg-purple-100 text-purple-800',
      both: 'bg-green-100 text-green-800',
    };
    return <Badge className={colors[scope] || ''}>{scope}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="ml-3 text-muted-foreground">Loading document settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Editing scope. Categories and types are always organization-wide;
            only the sub-type level is per-program, so this selector changes what
            the sub-type lists mean rather than filtering the page. */}
        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Configuring</Label>
          <Select
            value={activeProgramId === null ? 'org' : String(activeProgramId)}
            onValueChange={(v) => setActiveProgramId(v === 'org' ? null : Number(v))}
          >
            <SelectTrigger className="w-72"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="org">Organization catalog</SelectItem>
              {programs.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openCreateCategory}>
          <Plus className="w-4 h-4 mr-2" /> Add Category
        </Button>
      </div>

      {activeProgramId !== null && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-muted/50 text-sm">
          <Layers className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
          <span>
            Editing <strong>{programs.find((p) => p.id === activeProgramId)?.name}</strong>.
            Sub-type lists below now show what this program offers, and what it calls,
            requires and when it wants each one. Categories and document types stay
            organization-wide.
          </span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={() => setError(null)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Categories List */}
      <div className="space-y-3">
        {categories.map((cat) => (
          <Card key={cat.id} className={!cat.is_active ? 'opacity-60' : ''}>
            <CardHeader className="py-3 cursor-pointer" onClick={() => toggleCategory(cat.id)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {expandedCategories.has(cat.id) ? (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  )}
                  <FolderOpen className="w-5 h-5 text-primary" />
                  <div>
                    <CardTitle className="text-base">{cat.name}</CardTitle>
                    {cat.description && (
                      <CardDescription className="text-sm">{cat.description}</CardDescription>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {scopeBadge(cat.scope)}
                  {/* A category with no document types is still uploadable — the
                      server files those documents under an auto-created
                      "General" type (TRUS-436). Worth flagging so an admin who
                      meant to configure a real catalog notices, but this is
                      information, not a fault. */}
                  {cat.doc_type_count === 0 ? (
                    <Badge
                      variant="info"
                      title={
                        'No document types. Uploads to this category are filed under an ' +
                        'automatically created "General" type — add types here to categorize them.'
                      }
                    >
                      No types
                    </Badge>
                  ) : (
                    <Badge variant="secondary">{cat.doc_type_count} types</Badge>
                  )}
                  {!cat.is_active && <Badge variant="destructive">Inactive</Badge>}
                  <Button variant="ghost" size="sm" onClick={() => openEditCategory(cat)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deactivateCategory(cat.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            {expandedCategories.has(cat.id) && (
              <CardContent className="pt-0">
                <div className="border-t pt-3 space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground">Document Types</span>
                    <Button variant="outline" size="sm" onClick={() => openCreateType(cat.id)}>
                      <Plus className="w-3 h-3 mr-1" /> Add Type
                    </Button>
                  </div>

                  {(docTypes[cat.id] || []).map((dt) => (
                    // Wrapper, so the sub-type list can sit BELOW the type row
                    // rather than inside its flex layout.
                    <div key={dt.id}>
                    <div
                      className={`flex items-center justify-between p-3 rounded-md border ${
                        !dt.is_active ? 'opacity-60 bg-muted/50' : 'bg-background'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium text-sm">{dt.name}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                            {scopeBadge(dt.scope)}
                            {dt.metadata_fields.length > 0 && (
                              <span>{dt.metadata_fields.length} fields</span>
                            )}
                            {dt.document_count > 0 && (
                              <span>{dt.document_count} docs</span>
                            )}
                            {/* Always rendered, including the 'none' case. Showing
                                nothing there made a type that cannot attribute to a
                                program or enrollment indistinguishable from one an
                                admin simply had not looked at yet — which is how 27
                                of Maui's 37 types sat at ["none"] unnoticed. */}
                            <span className="flex items-center gap-1">
                              <Settings2 className="w-3 h-3" />
                              {dt.attribution_levels.filter((l) => l !== 'none').length > 0
                                ? dt.attribution_levels.filter((l) => l !== 'none').join(', ')
                                : 'no attribution'}
                            </span>
                            {(dt.program_ids || []).length > 0 && (
                              <span className="flex items-center gap-1" title={
                                dt.program_ids
                                  .map((id) => programs.find((p) => p.id === id)?.name || `Program ${id}`)
                                  .join(', ')
                              }>
                                <FolderOpen className="w-3 h-3" />
                                {dt.program_ids.length === 1
                                  ? (programs.find((p) => p.id === dt.program_ids[0])?.name || '1 program')
                                  : `${dt.program_ids.length} programs`}
                              </span>
                            )}
                            {dt.requires_hard_copy_workflow && (
                              <Badge variant="info" size="sm">hard-copy</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!dt.is_active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                        <Button variant="ghost" size="sm" onClick={() => openEditType(dt)}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deactivateDocType(dt)}>
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                        </div>
                      </div>
                      <DocSubtypeList
                        docTypeId={dt.id}
                        docTypeName={dt.name}
                        programId={activeProgramId}
                        programs={programs}
                        onError={setError}
                      />
                    </div>
                  ))}

                  {(docTypes[cat.id] || []).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No document types yet. Click "Add Type" to create one.
                    </p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        ))}

        {categories.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FolderOpen className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No document categories configured yet.</p>
              <Button onClick={openCreateCategory}>
                <Plus className="w-4 h-4 mr-2" /> Create First Category
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ================================================================ */}
      {/* CATEGORY DIALOG */}
      {/* ================================================================ */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'New Document Category'}</DialogTitle>
            <DialogDescription>
              Categories group related document types for easy navigation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Health Records"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Optional description..."
              />
            </div>
            <div>
              <Label>Scope *</Label>
              <Select
                value={categoryForm.scope}
                onValueChange={(v: any) => setCategoryForm({ ...categoryForm, scope: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member Records</SelectItem>
                  <SelectItem value="staff">Staff Records</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveCategory} disabled={!categoryForm.name}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================ */}
      {/* DOC TYPE DIALOG */}
      {/* ================================================================ */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Document Type' : 'New Document Type'}</DialogTitle>
            <DialogDescription>
              Configure the document type, metadata fields, access roles, and attribution rules.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <FileText className="w-4 h-4" /> Basic Information
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={typeForm.name}
                    onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                    placeholder="e.g., NP Authorization"
                  />
                </div>
                <div>
                  <Label>Scope</Label>
                  <Select
                    value={typeForm.scope}
                    onValueChange={(v: any) => setTypeForm({ ...typeForm, scope: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={typeForm.description}
                  onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
                  placeholder="Optional description..."
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={typeForm.is_notes_required}
                  onCheckedChange={(v) => setTypeForm({ ...typeForm, is_notes_required: v })}
                />
                <Label>Require notes on upload</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={typeForm.requires_hard_copy_workflow || false}
                  onCheckedChange={(v) => setTypeForm({ ...typeForm, requires_hard_copy_workflow: v })}
                />
                <Label>Enable hard-copy tracking workflow</Label>
                <span className="text-xs text-muted-foreground">(print, sign, upload cycle)</span>
              </div>
            </div>

            {/* Attribution Levels */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Settings2 className="w-4 h-4" /> Attribution
              </h4>
              <p className="text-xs text-muted-foreground">
                What a document of this type can be attached to. Leaving Program or
                Enrollment unticked does not just hide a picker — the attribution is
                dropped from the upload, so the document cannot be tied to the
                enrollment it belongs to.
              </p>
              <div className="flex flex-wrap gap-3">
                {['none', 'program', 'enrollment', 'appointment'].map((level) => (
                  <label key={level} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={typeForm.attribution_levels.includes(level)}
                      onCheckedChange={() => toggleAttribution(level)}
                    />
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </label>
                ))}
              </div>
            </div>

            {/* Program Availability */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <FolderOpen className="w-4 h-4" /> Program Availability
              </h4>
              {programs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No active programs are configured for this organization, so this
                  document type is offered everywhere.
                </p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    {(typeForm.program_ids || []).length === 0
                      ? 'Available to every program. Tick one or more to offer this type only where it applies — and to have it requested automatically when a member is enrolled in them.'
                      : 'Offered only for the ticked programs, and requested automatically when a member is enrolled in them. Untick them all to make it available everywhere again.'}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {programs.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={(typeForm.program_ids || []).includes(p.id)}
                          onCheckedChange={() => toggleProgram(p.id)}
                        />
                        <span className="truncate" title={p.name}>{p.name}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Metadata Fields */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium flex items-center gap-2">
                  <Settings2 className="w-4 h-4" /> Metadata Fields
                </h4>
                <Button variant="outline" size="sm" onClick={addMetadataField}>
                  <Plus className="w-3 h-3 mr-1" /> Add Field
                </Button>
              </div>

              {typeForm.metadata_fields.map((field: MetadataField, index: number) => (
                <div key={field.field_id} className="flex items-start gap-2 p-3 border rounded-md">
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Label</Label>
                      <Input
                        value={field.label}
                        onChange={(e) => updateMetadataField(index, { label: e.target.value })}
                        placeholder="Field label"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select
                        value={field.field_type}
                        onValueChange={(v) => updateMetadataField(index, { field_type: v })}
                      >
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="textarea">Long Text</SelectItem>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="dropdown">Dropdown</SelectItem>
                          <SelectItem value="checkbox">Checkbox</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end gap-2">
                      <label className="flex items-center gap-1 text-xs">
                        <Checkbox
                          checked={field.required}
                          onCheckedChange={(v) =>
                            updateMetadataField(index, { required: !!v })
                          }
                        />
                        Required
                      </label>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-5"
                    onClick={() => removeMetadataField(index)}
                  >
                    <X className="w-3 h-3 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>

            {/* File Constraints */}
            <div className="space-y-3">
              <h4 className="font-medium">File Constraints</h4>
              <div>
                <Label className="text-sm mb-2 block">Allowed File Types</Label>
                <div className="grid grid-cols-4 gap-x-4 gap-y-1.5">
                  {[
                    { ext: 'pdf', label: 'PDF' },
                    { ext: 'jpg', label: 'JPG' },
                    { ext: 'jpeg', label: 'JPEG' },
                    { ext: 'png', label: 'PNG' },
                    { ext: 'heic', label: 'HEIC' },
                    { ext: 'gif', label: 'GIF' },
                    { ext: 'doc', label: 'DOC' },
                    { ext: 'docx', label: 'DOCX' },
                    { ext: 'xls', label: 'XLS' },
                    { ext: 'xlsx', label: 'XLSX' },
                    { ext: 'ppt', label: 'PPT' },
                    { ext: 'pptx', label: 'PPTX' },
                    { ext: 'csv', label: 'CSV' },
                    { ext: 'txt', label: 'TXT' },
                    { ext: 'rtf', label: 'RTF' },
                    { ext: 'webp', label: 'WEBP' },
                  ].map(({ ext, label }) => (
                    <label key={ext} className="flex items-center gap-1.5 text-xs">
                      <Checkbox
                        checked={typeForm.allowed_file_types.includes(ext)}
                        onCheckedChange={(checked) => {
                          const types = checked
                            ? [...typeForm.allowed_file_types, ext]
                            : typeForm.allowed_file_types.filter((t: string) => t !== ext);
                          setTypeForm({ ...typeForm, allowed_file_types: types });
                        }}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Max File Size (MB)</Label>
                  <Input
                    type="number"
                    value={typeForm.max_file_size_mb || ''}
                    onChange={(e) =>
                      setTypeForm({
                        ...typeForm,
                        max_file_size_mb: e.target.value ? parseInt(e.target.value) : null,
                      })
                    }
                    placeholder="Default (50 MB)"
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveDocType} disabled={!typeForm.name}>
              {editingType ? 'Update' : 'Create'} Document Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
